import { useEffect, useRef } from 'react';
import { replace } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { REDIRECT_MS, SSO_ERROR_REDIRECTING_IN_5_SECONDS } from 'common/constants/sso';
import { ErrorComponent as TransitionComponent } from 'components';
import { useReadFragmentFromUrl } from 'hooks';
import { useGetUserInfo } from 'services/hooks/auth';
import { useExchangeToken } from './hooks';

// Minimal page that redeems SSO exchange ticket from fragment `#e=<ticket>`
const SsoExchange = () => {
  const navigate = useNavigate();
  const { exchangeToken: exchangeTokenFn, tokenExchangeHasFailed, isLoading } = useExchangeToken();
  const { fragmentRef } = useReadFragmentFromUrl();
  const getUserInfo = useGetUserInfo();
  const hasBeenRedeemed = useRef(false);

  useEffect(() => {
    if (!fragmentRef.current) {
      navigate('/'); // No ticket found, redirect to login
    }
    const redeem = async () => {
      const response = await exchangeTokenFn(fragmentRef.current!);
      if (response) {
        localStorage.setItem('access', response.access);
        localStorage.setItem('refresh', response.refresh);
        await getUserInfo();
        // Successfully exchanged token, redirect to root if response doesn't indicate where to
        const navigateTo = response.redirect_to || '/';
        navigate(navigateTo, { replace: true });
      }
    };
    if (!hasBeenRedeemed.current && fragmentRef.current) {
      hasBeenRedeemed.current = true;
      redeem();
    }
  }, [navigate, exchangeTokenFn, getUserInfo, fragmentRef]);

  useEffect(() => {
    if (tokenExchangeHasFailed) {
      // set timeout to redirect to login page after showing error
      const timer = setTimeout(() => {
        navigate('/');
      }, REDIRECT_MS);
      return () => clearTimeout(timer);
    }
  }, [tokenExchangeHasFailed, navigate]);

  if (!isLoading && tokenExchangeHasFailed) {
    return <TransitionComponent title=":(" subtitle="SSO Login Failed" body={SSO_ERROR_REDIRECTING_IN_5_SECONDS} />;
  }

  return <TransitionComponent title="..." subtitle="Processing SSO response" body="Please wait a moment..." />;
};

export default SsoExchange;
